import TextField from '@/components/skeletons/TextField'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { getServiceContract } from '@/modules/Service/services'
import { formatDate } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import React from 'react'
import { Link } from 'react-router-dom'
import { Chip, Tooltip, Typogaphy } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { Color } from '@/components/GokuDataGrid/CellStatus'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface ServiceContractProps {
    serviceID: number
}
const ServiceContract: React.FC<ServiceContractProps> = ({ serviceID }) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONTRACTS, serviceID],
        queryFn: () => getServiceContract(serviceID),
        loadingComponent: <TextField />,
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <>
            {data && data.length > 0 ? (
                <div className="flex gap-1">
                    {data.map((contract, index) => {
                        const status = CONTRACT_STATUS(t)?.find(
                            (item) => item.value === contract.contract_status
                        )
                        // A service can reach a contract through an annex (phụ lục) instead of
                        // directly. Show the parent contract number, plus the annex code so the
                        // link is traceable to the document that actually holds the terms.
                        const annexCode =
                            contract.link_type === 'subcontract' &&
                            contract.subcontract_id
                                ? contract.subcontract_code
                                : null
                        return (
                            // The annex chip is a SIBLING of the contract chip, not nested with it:
                            // both sit directly in the outer `flex gap-1`, which is the spacing this
                            // column has always used between chips. The contract chip below keeps
                            // its original wrapper untouched so a service with no annex renders
                            // exactly as it did before.
                            <React.Fragment key={index}>
                                <div className="inline-flex">
                                    <Tooltip
                                        className=""
                                        title={
                                            contract.contract_effective_date &&
                                            contract.contract_expiry_date
                                                ? `${formatDate(contract.contract_effective_date)} - ${formatDate(contract.contract_expiry_date)}`
                                                : t('no-time')
                                        }
                                    >
                                        <Link
                                            to={generatePathDatacentral(
                                                paths.contractDetail,
                                                {
                                                    id: contract.contract_id,
                                                }
                                            )}
                                            replace
                                        >
                                            <Chip
                                                size="small"
                                                className="cursor-pointer inline-flex"
                                                variant="outlined"
                                                color={status?.color as Color}
                                                label={
                                                    contract.contract_number_id
                                                }
                                            />
                                        </Link>
                                    </Tooltip>
                                </div>
                                {annexCode && (
                                    <div className="inline-flex">
                                        <Link
                                            to={generatePathDatacentral(
                                                paths.subContractDetail,
                                                {
                                                    contractID:
                                                        contract.contract_id,
                                                    subcontractID:
                                                        contract.subcontract_id as number,
                                                }
                                            )}
                                            replace
                                        >
                                            <Chip
                                                size="small"
                                                className="cursor-pointer inline-flex"
                                                variant="outlined"
                                                color={status?.color as Color}
                                                label={annexCode}
                                            />
                                        </Link>
                                    </div>
                                )}
                            </React.Fragment>
                        )
                    })}
                </div>
            ) : (
                <Typogaphy size="small"> --- </Typogaphy>
            )}
        </>
    )
}

export default ServiceContract
